import CryptoJS from "crypto-js";

interface LoginCredentials {
  loginName: string;
  password: string;
  ip: string;
  os: string;
  deviceType?:number;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: string; // encrypted data string
}

interface DecryptedData {
  [key: string]: any;
}

const BASE_URL = import.meta.env.DEV ? "" : (import.meta.env.VITE_API_BASE_URL || "");
const DECRYPTION_KEY = "1234567890abcdef1234567890abcdef";
const DECRYPTION_IV = "1234567890abcdef";

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await fetch(`${BASE_URL}/pabws/users/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Login API error:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "Login failed",
        data: "",
      };
    }
  },

  encryptData(plain: unknown): string | null {
    try {
      const text = typeof plain === "string" ? plain : JSON.stringify(plain);
      // Try with hex-parsed key/iv first (same as decrypt default)
      try {
        const keyHex = CryptoJS.enc.Hex.parse(DECRYPTION_KEY);
        const ivHex = CryptoJS.enc.Hex.parse(DECRYPTION_IV);
        const cipher = CryptoJS.AES.encrypt(text, keyHex, {
          iv: ivHex,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        });
        return cipher.toString(); // Base64
      } catch (e1) {
        const keyUtf8 = CryptoJS.enc.Utf8.parse(DECRYPTION_KEY);
        const ivUtf8 = CryptoJS.enc.Utf8.parse(DECRYPTION_IV);
        const cipher = CryptoJS.AES.encrypt(text, keyUtf8, {
          iv: ivUtf8,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        });
        return cipher.toString();
      }
    } catch (error) {
      console.error("Encryption error:", error);
      return null;
    }
  },

  decryptData(encryptedData: string): DecryptedData | null {
    const normalizeBase64Url = (input: string): string => {
      let s = (input || "").replace(/-/g, "+").replace(/_/g, "/");
      const mod = s.length % 4;
      if (mod) s = s.padEnd(s.length + (4 - mod), "=");
      return s;
    };

    const tryDecrypt = (
      cipherB64: string,
      keyParser: (v: string) => CryptoJS.lib.WordArray,
      ivParser: (v: string) => CryptoJS.lib.WordArray
    ) => {
      const key = keyParser(DECRYPTION_KEY);
      const iv = ivParser(DECRYPTION_IV);
      const bytes = CryptoJS.AES.decrypt(cipherB64, key, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      const text = bytes.toString(CryptoJS.enc.Utf8);
      if (!text)
        throw new Error("Empty plaintext (likely wrong key/iv or format)");
      return text;
    };

    try {
      const normalized = normalizeBase64Url(encryptedData);

      // Attempt 1: Keys interpreted as hex (current assumption)
      try {
        const plain = tryDecrypt(
          normalized,
          CryptoJS.enc.Hex.parse,
          CryptoJS.enc.Hex.parse
        );
        return JSON.parse(plain);
      } catch (e1) {
        // Attempt 2: Keys interpreted as UTF-8 bytes (some servers provide ASCII keys)
        try {
          const plain = tryDecrypt(
            normalized,
            CryptoJS.enc.Utf8.parse,
            CryptoJS.enc.Utf8.parse
          );
          return JSON.parse(plain);
        } catch (e2) {
          // Last resort: return raw string if it's not JSON but valid UTF-8
          try {
            const plain = tryDecrypt(
              normalized,
              CryptoJS.enc.Utf8.parse,
              CryptoJS.enc.Utf8.parse
            );
            return { value: plain } as unknown as DecryptedData;
          } catch (e3) {
            console.error("Decryption error: all strategies failed", {
              e1,
              e2,
              e3,
            });
            return null;
          }
        }
      }
    } catch (error) {
      console.error("Decryption error:", error);
      return null;
    }
  },
};
